import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon, Button } from '@ontruck/orc';
import { postAssignVehicles, putAssignDriver } from 'utils/network/http';
import { useTranslation } from 'react-i18next';
import {
  VEHICLE_ICONS,
  VEHICLE_TYPES,
  MIDDLE_SELECTION_OPTIONS,
} from 'utils/constants/vehicles';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import Dropdown from 'components/Dropdown';
import { useAssignment } from 'hooks/useAssignment/useAssignment';
import JobActionsOption from 'components/JobActions/JobActionsOption';
import Spinner from 'components/Spinner';
import { useVehicles } from 'hooks/useVehicles/useVehicles';
import { useAuth } from 'hooks/useAuth/useAuth';

const JobActions = ({
  className,
  assignedDriverId,
  assignedVehiclesIds,
  silentFetchJourney,
  journeyId,
  colleagues,
  driverConfirmed,
}) => {
  const { t } = useTranslation('PendingJobDetail');
  const [loadingAssignDriver, setLoadingAssignDriver] = useState(false);
  const [loadingAssignVehicle, setLoadingAssignVehicle] = useState(false);
  const [error, setError] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState(assignedVehiclesIds);
  const [isVehicleSecondChoice, setIsVehicleSecondChoice] = useState(false);
  const [showVehiclesDropdown, setShowVehiclesDropdown] = useState(false);
  const [showDriversDropdown, setShowDriversDropdown] = useState(false);
  const [vehiclesReadyToSend, setVehiclesReadyToSend] = useState(false);
  const [middleSelection, setMiddleSelection] = useState(null);
  const {
    handleShowFastVehicleCreation,
    suitableVehicles,
    loadingSuitableVehicles,
    isTrailerJob,
  } = useVehicles();

  const {
    driverName,
    vehiclesPlates,
    pendingVehicleLabel,
    findVehicle,
  } = useAssignment(assignedDriverId, assignedVehiclesIds, isTrailerJob);
  const { profile } = useAuth();

  const checkIfItisInMidleSelection = useCallback(() => {
    if (!isTrailerJob || selectedVehicles.length !== 1) return null;
    const vehicleType = findVehicle(selectedVehicles[0])?.vehicle_type;
    const middleOptions = {
      [VEHICLE_TYPES.tractor_unit]: MIDDLE_SELECTION_OPTIONS.tractor,
      [VEHICLE_TYPES.trailer]: MIDDLE_SELECTION_OPTIONS.trailer,
    };
    return middleOptions[vehicleType] || null;
  }, [isTrailerJob, selectedVehicles, findVehicle]);

  const closeVehiclesDropdown = useCallback(() => {
    setShowVehiclesDropdown(false);
  }, []);

  const closeDriversDropdown = useCallback(() => {
    setShowDriversDropdown(false);
  }, []);

  const handleAssignDriver = useCallback(
    async (driverId) => {
      setShowDriversDropdown(false);
      try {
        setLoadingAssignDriver(true);
        setError(false);
        sendEventToSegment(TRACKING_EVENTS.DRIVER_ASSIGN, {
          driverId,
          journeyId,
        });
        await putAssignDriver(journeyId, driverId);
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        silentFetchJourney();
        setLoadingAssignDriver(false);
      }
    },
    [journeyId, silentFetchJourney]
  );

  const handleAssignVehicle = useCallback(
    (vehicleId, vehicleType) => {
      if (
        vehicleType === 'tractor_unit' &&
        !isVehicleSecondChoice &&
        !middleSelection
      ) {
        setIsVehicleSecondChoice(true);
        setSelectedVehicles([vehicleId]);
        return;
      }
      if (
        (isVehicleSecondChoice && vehicleType === 'trailer') ||
        middleSelection !== null
      ) {
        setIsVehicleSecondChoice(false);
        setShowVehiclesDropdown(false);
        setSelectedVehicles([...selectedVehicles, vehicleId]);
        setVehiclesReadyToSend(true);
        return;
      }

      setSelectedVehicles([vehicleId]);
      setVehiclesReadyToSend(true);
      setShowVehiclesDropdown(false);
    },
    [isVehicleSecondChoice, selectedVehicles, middleSelection]
  );

  const assignVehicles = useCallback(async () => {
    try {
      setLoadingAssignVehicle(true);
      setError(false);
      sendEventToSegment(TRACKING_EVENTS.VEHICLE_ASSIGN, {
        vehicles: selectedVehicles,
        journeyId,
      });
      await postAssignVehicles(journeyId, selectedVehicles);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      silentFetchJourney();
      setVehiclesReadyToSend(false);
      setLoadingAssignVehicle(false);
    }
  }, [journeyId, selectedVehicles, silentFetchJourney]);

  useEffect(() => {
    if (vehiclesReadyToSend && !loadingSuitableVehicles && !error) {
      assignVehicles();
    }
  }, [vehiclesReadyToSend, assignVehicles, loadingSuitableVehicles, error]);

  useEffect(() => {
    setMiddleSelection(checkIfItisInMidleSelection());
  }, [selectedVehicles, checkIfItisInMidleSelection]);

  const renderDriversList = useCallback(() => {
    if (!colleagues || colleagues.length === 0) {
      return (
        <div
          className="job-actions__no-items"
          data-testis="job-actions__no-drivers"
        >
          {t('noSuitableDrivers')}
        </div>
      );
    }
    return colleagues
      .filter(
        (driver) =>
          driver.is_active && !driver.is_blocked && driver.status === 'active'
      )
      .map((driver) => {
        return (
          <li
            data-testid="job-actions__driverItem"
            className="job-actions__dropdown-item"
            key={`vehicle-${driver.id}`}
            onClick={() => handleAssignDriver(driver.id)}
          >
            <div className="job-actions__dropdown-item-icon">
              <Icon glyph="status-driver" size="4rem" />
            </div>
            <div className="job-actions__dropdown-item-text">
              <div className="job-actions__dropdown-item-plate">
                {driver.contact_name}
              </div>
            </div>
          </li>
        );
      });
  }, [handleAssignDriver, colleagues, t]);

  const renderVehiclesList = useCallback(() => {
    if (!suitableVehicles || suitableVehicles.length === 0) {
      return (
        <div className="job-actions__no-items">{t('noSuitableVehicles')}</div>
      );
    }

    const filteredSuitableVehicles = suitableVehicles.filter(
      (vehicle) =>
        (isVehicleSecondChoice ||
        middleSelection === MIDDLE_SELECTION_OPTIONS.tractor
          ? vehicle.vehicle_type === 'trailer'
          : vehicle.vehicle_type !== 'trailer') && vehicle.is_suitable
    );

    if (filteredSuitableVehicles.length === 0) {
      return (
        <div className="job-actions__no-items">{t('noSuitableVehicles')}</div>
      );
    }

    return filteredSuitableVehicles.map((vehicle) => {
      return (
        <li
          data-testid="job-actions__vehicleItem"
          className="job-actions__dropdown-item"
          key={`vehicle-${vehicle.id}`}
          onClick={() => handleAssignVehicle(vehicle.id, vehicle.vehicle_type)}
        >
          <div className="job-actions__dropdown-item-icon">
            <Icon glyph={VEHICLE_ICONS[vehicle.vehicle_type]} size="4rem" />
          </div>
          <div className="job-actions__dropdown-item-text">
            <div className="job-actions__dropdown-item-plate">
              {vehicle.plate_number}
            </div>
            <div className="job-actions__dropdown-item-type">
              {t(vehicle.vehicle_type)}
            </div>
          </div>
        </li>
      );
    });
  }, [
    handleAssignVehicle,
    isVehicleSecondChoice,
    suitableVehicles,
    t,
    middleSelection,
  ]);

  const handleFastVehicleCreation = useCallback(() => {
    handleShowFastVehicleCreation(true);
    closeVehiclesDropdown();
    sendEventToSegment(TRACKING_EVENTS.FAST_VEHICLE_FORM_OPEN, {
      journeyId,
      carrierId: profile?.carrier.id,
    });
  }, [
    closeVehiclesDropdown,
    handleShowFastVehicleCreation,
    journeyId,
    profile,
  ]);

  return (
    <div className={className} data-testid="job-actions">
      <div className="job-actions">
        {!loadingSuitableVehicles && (
          <div className="job-actions__options">
            <JobActionsOption
              loading={loadingAssignDriver}
              notAssignedLabel={t('driverNotAssigned')}
              assignedLabel={
                driverConfirmed && assignedDriverId ? driverName : null
              }
              onClick={() => {
                setShowDriversDropdown(!showDriversDropdown);
                setShowVehiclesDropdown(false);
              }}
              testId="driver"
            />
            <JobActionsOption
              loading={loadingAssignVehicle}
              notAssignedLabel={t('vehicleNotAssigned')}
              assignedLabel={vehiclesPlates}
              onClick={() => {
                setShowVehiclesDropdown(!showVehiclesDropdown);
                setShowDriversDropdown(false);
              }}
              testId="vehicle"
              pendingLabel={pendingVehicleLabel}
            />
          </div>
        )}
        {loadingSuitableVehicles && (
          <div className="job-actions__loading">
            <Spinner noBackdrop />
          </div>
        )}

        <Dropdown show={showVehiclesDropdown} onClose={closeVehiclesDropdown}>
          <div
            className="job-actions__dropdown"
            data-testid="job-actions__vehicles-dropdown"
          >
            {isTrailerJob && (
              <Button
                icon="plus"
                iconPosition="left"
                skin="primary"
                text={t('registerVehicle')}
                onClick={handleFastVehicleCreation}
                className="job-actions__dropdown-button"
                data-testid="job-actions__fast-vehicle-button"
              />
            )}
            <div className="job-actions__dropdown-title">
              {t('registeredVehicles')}
            </div>
            <ul className="job-actions__dropdown-list">
              {renderVehiclesList()}
            </ul>
          </div>
        </Dropdown>

        <Dropdown show={showDriversDropdown} onClose={closeDriversDropdown}>
          <div
            className="job-actions__tooltip"
            data-testid="job-actions__drivers-dropdown"
          >
            <ul className="job-actions__dropdown-list">
              {renderDriversList()}
            </ul>
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

JobActions.defaultProps = {
  className: '',
  assignedDriverId: null,
  assignedVehiclesIds: [],
  colleagues: [],
  driverConfirmed: false,
};

JobActions.propTypes = {
  className: PropTypes.string,
  assignedDriverId: PropTypes.number,
  assignedVehiclesIds: PropTypes.array,
  silentFetchJourney: PropTypes.func.isRequired,
  journeyId: PropTypes.number.isRequired,
  colleagues: PropTypes.array,
  driverConfirmed: PropTypes.bool,
};

export default JobActions;
